import { uuid as uuidv4 } from './browser';
import { TRACKING_COOKIES } from '../constants';
import { readCookie, writeCookie } from './cookies';

/**
 * This function will parse the hash for a SKU and call the merchant's add-to-cart handler
 * with the SKU. If the handler returns true, the function will call the merchant's checkout
 * handler.
 *
 * This allows for a user to click a link with a hash like #bamatc_sku=1234 to add an item to cart.
 * Several other `bamatc_` prefixed query params are used for metrics as well as `utm_` prefixed query params.
 *
 * Note: The reason we're not using a query param is because the merchant's add to cart handler
 * might POST to the same URL the page is served on, which may not approve of the query param.
 * (This has been observed)
 */
const installHashAddToCartHandler = async (window, BambuserLiveShopping) => {
  const hasBamATCFrag = /[&#]bamatc_\w+=(.+)/.test(window.location.hash);
  if (!hasBamATCFrag) {
    // Check for add to cart sku as query param
    const url = new URL(window.location.href);
    if (url.searchParams?.has('bamatc_sku')) {
      // Convert entries Iterator to array, since
      // deleting from the searchParams will affect the iterator
      url.hash = [...url.searchParams.entries()]
        // Only remove bamatc_ or utm_ prefixed query params
        .filter(([key]) => key.startsWith('bamatc_') || key.startsWith('utm_'))
        .reduce((hash, [key, value]) => {
          url.searchParams.delete(key);
          return hash
            ?`${hash}&${key}=${value}`
            :`${key}=${value}`;
        }, url.hash);
      window.location.href = url.toString();
    }
    return;
  }
  if (!window.Proxy) return; // No support for Proxy, can't mock the player

  // Wait for onBambuserLiveShoppingReady to be available
  await (async () => {
    let tries = 100;
    const tick = async () => {
      if (window.onBambuserLiveShoppingReady) return;
      if (tries-- > 0) {
        await new Promise((res) => window.setTimeout(res, 100));
        return tick();
      }
    };
    await tick();
  })();

  if (!window.onBambuserLiveShoppingReady) return;

  // Mock the Bambuser Live Shopping player object using a proxy
  const createNoOpProxy = (targetObject = {}) => {
    return new window.Proxy(targetObject, {
      get: (target, prop) => {
        if (prop in target) {
          return target[prop]; // Return existing property from target object
        }

        // Mock functions return no-op, non-functions return empty objects
        return function(...args) {
          return typeof args[0] === 'function' ? () => {} : {};
        };
      }
    });
  };

  const callbacks = {}; // Store event callbacks the site will register via player.on()

  const player = {
    EVENT: {
      ADD_TO_CART: 'livecommerce:should-add-item-to-cart',
      CHECKOUT: 'livecommerce:goto-checkout',
    },
    showCheckout: (url) => {
      if (url?.match(/^https?:\/\//)) {
        window.location.href = url;
      }
    },
    on: (event, callback) => {
      callbacks[event] = callback;
    },
  };
  const proxyPlayer = createNoOpProxy(player);

  // Call the onBambuserLiveShoppingReady callback with the proxy player
  await window.onBambuserLiveShoppingReady(proxyPlayer);

  if (typeof callbacks[player.EVENT.ADD_TO_CART] !== 'function') {
    // If the site hasn't registered a callback for ADD_TO_CART, do nothing
    return;
  }

  const {
    bamatc_sku: sku,
    bamatc_orgId: orgId,
    bamatc_showId: showId,
    bamatc_productId: productId,
    bamatc_platform: fromPlatform,
    bamatc_variation: variation = null,
    bamatc_title: title = null,
    bamatc_size: size = null,
    bamatc_remoteAssetId: remoteAssetId = null,
    bamatc_remoteAssetName: remoteAssetName = null,
    bamatc_remoteAccountId: remoteAccountId = null,
    bamatc_remoteAccountName: remoteAccountName = null,
  } = Object.fromEntries(
    [...new URLSearchParams(window.location.hash.slice(1)).entries()].map(i => i.map(decodeURIComponent))
  );

  if (!sku) {
    // If no SKUs are found, do nothing
    return;
  }

  // If the site has registered a callback for ADD_TO_CART, call it with the SKU from the hash param
  const addToCartResult = await new Promise((resolve) => {
    // Set a timeout to resolve the promise after 5 seconds if no response is received
    const timeoutHandle = window.setTimeout(() => resolve(false), 5000);

    callbacks[player.EVENT.ADD_TO_CART]({ sku }, (result) => {
      if (!result) {
        console.error(`Failed to add item with SKU ${sku} to cart`);
      }
      window.clearTimeout(timeoutHandle);
      return resolve(true); // True to differentiate from the timeout
    });
  });

  if (!addToCartResult) {
    // If the add to cart failed, do nothing
    return;
  }

  try {
    const existingUserId = readCookie(TRACKING_COOKIES.USER_ID);
    const existingSessionId = readCookie(TRACKING_COOKIES.SESSION_ID);
    const userId = existingUserId || uuidv4();
    const sessionId = existingSessionId || uuidv4();

    const cookieConf = {
      expires: 365,
      domain: window.location.hostname,
    };

    if (orgId) {
      writeCookie(TRACKING_COOKIES.CUSTOMER_ID, orgId, cookieConf);
    }

    if (!existingUserId) {
      writeCookie(TRACKING_COOKIES.USER_ID, userId, cookieConf);
    }

    if (!existingSessionId) {
      writeCookie(TRACKING_COOKIES.SESSION_ID, sessionId, cookieConf);
    }

    // Track metrics
    await BambuserLiveShopping.prototype._trackMetricEmit({
      orgId,
      showId,
      fromPlatform,
      userId,
      sessionId,
      source: 'embed',
      eventType: 'on-remote-add-to-cart',
      insertId: uuidv4(),
      remoteAssetId,
      remoteAssetName,
      remoteAccountId,
      remoteAccountName,
      items: [{
        id: productId,
        vendor: "hydratable-product",
        sku,
        quantity: 1,
        title,
        size,
        variation,
        // Price is not available here
      }],
    });
  } catch (error) {
    console.error('Failed to track add to cart metrics', error.message);
  }

  // If the site has registered a callback for CHECKOUT, call it to navigate to the checkout
  callbacks[player.EVENT.CHECKOUT]?.();
};

export {
  installHashAddToCartHandler,
};
